// Вкладка «Коды активации»: выпустить коды (сколько дней дают, сколько активаций, сколько дней их можно ввести),
// список с использованием и отзывом. Коды за оплату звёздами бот выпускает сам (источник «Telegram»).
import { useState } from 'react';
import { ctx } from '../../core/ctx';
import { askConfirm } from '../../ui/prompts';
import { Btn, Field } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { adminApi, type ACode } from './adminApi';
import { act, copyText, CopyField, fmtDate, fmtLeft, LoadError, Pill, useLoad } from './common';

function state(c: ACode): { tone: 'ok' | 'dim'; text: string } {
  if (c.revoked) return { tone: 'dim', text: 'Отозван' };
  if (c.expires && c.expires < Date.now() / 1000) return { tone: 'dim', text: 'Истёк' };
  if (c.maxUses && c.uses >= c.maxUses) return { tone: 'dim', text: 'Использован' };
  return { tone: 'ok', text: 'Действует' };
}

export function CodesTab() {
  const { data, error, reload } = useLoad(() => adminApi.codes(), []);
  const [lifetime, setLifetime] = useState(false);
  const [days, setDays] = useState('30');
  const [uses, setUses] = useState('1');
  const [valid, setValid] = useState('30');
  const [count, setCount] = useState('1');
  const [note, setNote] = useState('');
  const [price, setPrice] = useState('0');
  const [made, setMade] = useState<ACode[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const r = await adminApi.createCodes({ days: lifetime ? 0 : Number(days), maxUses: Number(uses), validDays: valid.trim() ? Number(valid) : null, note: note.trim(), count: Number(count), paidRub: Number(price) || 0 });
      setMade(r.codes);
      setNote('');
      await reload();
    } catch (e: any) { ctx.toast(e.message, 'error', 8000); } finally { setBusy(false); }
  };
  const all = data?.codes || [];
  const active = all.filter((c) => state(c).tone === 'ok');
  const list = showAll ? all : active;
  return (
    <section className="adm-tab" data-testid="admin-codes">
      <div className="adm-card adm-invite-new" data-testid="code-form">
        <div className="adm-card-title"><Icon name="key" size={17} /> Новый код активации</div>
        <div className="adm-invite-grid">
          <Field label="Сколько дней даёт код" hint="прибавляется к текущему сроку подписки">
            <span className="adm-row">
              <input type="number" min={1} value={days} disabled={lifetime} onChange={(e) => setDays(e.target.value)} data-testid="code-days" />
              <label className="check small"><input type="checkbox" checked={lifetime} onChange={(e) => setLifetime(e.target.checked)} data-testid="code-lifetime" /> пожизненная</label>
            </span>
          </Field>
          <Field label="Сколько активаций" hint="один человек активирует код один раз; 0 - без ограничения">
            <input type="number" min={0} value={uses} onChange={(e) => setUses(e.target.value)} data-testid="code-uses" />
          </Field>
          <Field label="Сколько дней код можно ввести" hint="пусто - бессрочно">
            <input type="number" min={1} value={valid} onChange={(e) => setValid(e.target.value)} data-testid="code-valid" />
          </Field>
          <Field label="Сколько кодов выпустить"><input type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} data-testid="code-count" /></Field>
          <Field label="Цена кода, ₽" hint="сколько за него заплатили; 0 - подарок: пригласившему за такой код ничего не начисляется">
            <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="code-price" />
          </Field>
          <Field label="Заметка (для себя)"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="напр. группа 151115" maxLength={200} data-testid="code-note" /></Field>
        </div>
        <div className="adm-row"><Btn icon="key" label="Выпустить" primary onClick={create} disabled={busy} testid="code-create" /></div>
        {made && (
          <div className="adm-invite-result" data-testid="code-result">
            <div className="adm-invite-result-head"><Icon name="ok" size={16} /> {made.length > 1 ? `Выпущено кодов: ${made.length}` : 'Код готов'}: передайте {made.length > 1 ? 'их' : 'его'} пользователям.</div>
            {made.length > 1
              ? <><textarea readOnly rows={Math.min(made.length, 8)} value={made.map((c) => c.code).join('\n')} className="mono" data-testid="code-list" />
                <Btn icon="copy" label="Скопировать все" onClick={async () => { ctx.toast(await copyText(made.map((c) => c.code).join('\n')) ? 'Скопировано' : 'Не удалось скопировать', 'ok'); }} /></>
              : <CopyField value={made[0].code} testid="code-value" />}
          </div>
        )}
      </div>

      <div className="adm-section-head">
        <span>Коды</span>
        <span className="topbar-spacer" />
        {all.length > active.length && <label className="check small"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="codes-all" /> показать недействующие ({all.length - active.length})</label>}
      </div>
      <LoadError error={error} reload={reload} />
      <div className="adm-table-wrap">
        <table className="adm-table" data-testid="codes-table">
          <thead><tr><th>Код</th><th>Даёт</th><th className="num">Активации</th><th>Ввести можно</th><th>Заметка</th><th>Состояние</th><th className="adm-col-act" /></tr></thead>
          <tbody>
            {list.map((c) => {
              const st = state(c);
              return (
                <tr key={c.id} data-testid="code-row" className={st.tone === 'dim' ? 'is-dim' : ''}>
                  <td><code className="adm-code">{c.code}</code>{c.source === 'stars' && <span className="adm-tag" title="Выдан ботом за оплату звёздами">Telegram</span>}<div className="adm-sub">создан {fmtDate(c.created)}</div></td>
                  <td>{c.lifetime ? 'пожизненную' : `${c.days} дн.`}{c.paidRub > 0 && <div className="adm-sub">цена {c.paidRub} ₽</div>}</td>
                  <td className="num" title={(c.usedBy || []).map((x) => x.login).join(', ')}>{c.uses}{c.maxUses ? ` / ${c.maxUses}` : ' / ∞'}
                    {(c.usedBy || []).length > 0 && <div className="adm-sub">{(c.usedBy || []).slice(0, 3).map((x) => x.login).join(', ')}{(c.usedBy || []).length > 3 ? '…' : ''}</div>}</td>
                  <td title={c.expires ? fmtDate(c.expires, true) : ''}>{c.expires ? fmtLeft(c.expires) : 'бессрочно'}</td>
                  <td>{c.note || <span className="adm-dim">—</span>}</td>
                  <td><Pill tone={st.tone}>{st.text}</Pill></td>
                  <td className="adm-col-act adm-actions">
                    {st.tone === 'ok' && <>
                      <Btn icon="copy" title="Скопировать код" onClick={async () => { ctx.toast(await copyText(c.code) ? 'Код скопирован' : 'Не удалось скопировать', 'ok'); }} testid="code-copy" />
                      <Btn icon="ban" title="Отозвать" danger onClick={async () => {
                        if (await askConfirm({ title: 'Отозвать код?', text: 'Его больше нельзя будет активировать. У тех, кто уже активировал, срок подписки не изменится.', ok: 'Отозвать', danger: true })) {
                          void act(() => adminApi.revokeCode(c.id), 'Код отозван', reload);
                        }
                      }} testid="code-revoke" />
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data && !list.length && <div className="adm-empty"><Icon name="key" size={26} /><div>Действующих кодов нет. Выпустите код выше.</div></div>}
      </div>
    </section>
  );
}
